import { Fragment } from 'react';
import { AVAILABILITY_IMAGE_CELL, AVAILABILITY_IMAGE_CELL_LABELS, AVAILABILITY_IMAGE_COMPACT_LEGEND } from '../../constants/bookingConst';
import { planCompactGridRows } from '../../utils/availabilitySnapshotUtil';
import '../../css/AvailabilityGuestGrid.css';
import '../../css/AvailabilityMultiDay.css';

/**
 * Slot class for one court-hour: its status, or promo for an open promo hour.
 * @param {{status: string, promo?: boolean}|undefined} cell
 * @returns {string}
 */
const pillClass = (cell) => {
  const status = cell?.status || AVAILABILITY_IMAGE_CELL.CLOSED;
  if (status === AVAILABILITY_IMAGE_CELL.OPEN && cell?.promo) return 'amc-pill amc-pill-open amc-pill-promo';
  return `amc-pill amc-pill-${status}`;
};

/**
 * The multi-day table on the phone-sized images: hours down the side, one
 * column per DATE, and in each cell a slot per court with its status word —
 * OPEN / BOOKED / OPEN PLAY / CLOSED, coloured as on the guest grid, promo
 * hours outlined in the accent colour.
 *
 * planCompactGridRows picks how a date's courts share its column: side by
 * side under a row of court names, or stacked in court order beside a column
 * of court numbers — whichever lets the words be bigger. The words' font size comes
 * from the image settings ("Slot labels") when the admin sets one.
 *
 * @param {object} props
 * @param {object} props.snapshot - buildMultiDaySnapshot() output
 * @param {{bodyHeight: number, tableWidth: number, minHourlyRowHeight: number, maxRowHeight: number}} props.sizing
 * @param {'ranges'|'hourly'} [props.rowMode]
 * @param {object} [props.imageSettings] - Availability images settings (font sizes in px, BOOKED colours)
 * @returns {JSX.Element}
 */
function MultiDayCompactTable({ snapshot, sizing, rowMode, imageSettings }) {
  const { days, courts: columns } = snapshot;
  const { rows, merged, rowHeight, stacked, timePx, wordPx, openPlayPx, dayPx } = planCompactGridRows(snapshot, {
    ...sizing,
    rowMode,
    textPx: { time: imageSettings?.timePx, cell: imageSettings?.cellPx },
  });
  const dayColumns = days.map(day => columns.filter(column => column.dayIndex === day.dayIndex));
  // Stacked slots carry only the court NUMBER; the key is worth its space
  // when the names say more than that number does.
  const firstDayCourts = dayColumns[0] || [];
  const showCourtKey = stacked && firstDayCourts.some(column => column.label !== `Court ${column.court}`);
  const showLegend = snapshot.hasPromo || showCourtKey;

  return (
    <div
      className={`agg-table amc-table${merged ? ' agg-table-merged' : ''}${stacked ? ' amc-stacked' : ''}`}
      style={{
        '--agg-booked-bg': imageSettings?.bookedBg || undefined,
        '--agg-booked-ink': imageSettings?.bookedText || undefined,
        '--amc-time-px': `${timePx}px`,
        '--amc-word-px': `${wordPx}px`,
        '--amc-op-px': `${openPlayPx}px`,
        '--amc-day-px': `${dayPx}px`,
      }}
    >
      <div
        className="amc-grid"
        style={{
          gridTemplateColumns: `max-content${stacked ? ' max-content' : ''} repeat(${Math.max(days.length, 1)}, minmax(0, 1fr))`,
          // Header rows (dates, and court names when side by side) take their
          // own height; every hour row after them is rowHeight.
          gridTemplateRows: stacked ? 'auto' : 'auto auto',
          gridAutoRows: `${rowHeight}px`,
        }}
      >
        <span className="amc-corner" style={stacked ? { gridColumn: 'span 2' } : { gridRow: 'span 2' }} />
        {days.map(day => (
          <span key={day.dateStr} className="amc-day">
            <span className="amc-day-name">{day.weekdayShortUpper}</span>
            <span className="amc-day-number">{day.dayNumber}</span>
          </span>
        ))}
        {/* Side by side: the court names, once per date, over their slots. */}
        {!stacked && dayColumns.map((dayCols, index) => (
          <span key={days[index].dateStr} className="amc-courts">
            {dayCols.map(column => (
              <span key={column.number} className="amc-court-name">{column.label}</span>
            ))}
          </span>
        ))}

        {rows.map(row => (
          <Fragment key={row.key}>
            <span className="amc-time">
              <span className="amc-time-range">{row.timeParts.start}{'–'}{row.timeParts.end}</span>
              <span className="amc-time-meridiem">
                {row.timeParts.startMeridiem === row.timeParts.endMeridiem
                  ? row.timeParts.endMeridiem
                  : `${row.timeParts.startMeridiem}–${row.timeParts.endMeridiem}`}
              </span>
              {row.isNextDay && <span className="amc-time-nextday">{snapshot.nextDayLabel}</span>}
            </span>
            {/* Stacked: the court numbers, lined up with the slots beside them. */}
            {stacked && (
              <span className="amc-cell amc-gutter" aria-hidden="true">
                {firstDayCourts.map(column => <span key={column.number} className="amc-gutter-num">{column.court}</span>)}
              </span>
            )}
            {dayColumns.map((dayCols, index) => (
              <span key={days[index].dateStr} className="amc-cell">
                {dayCols.map((column) => {
                  const cell = row.cells[column.number];
                  return (
                    <span key={column.number} className={pillClass(cell)}>
                      <span className="amc-word">{AVAILABILITY_IMAGE_CELL_LABELS[cell?.status] || AVAILABILITY_IMAGE_CELL_LABELS.closed}</span>
                    </span>
                  );
                })}
              </span>
            ))}
          </Fragment>
        ))}
      </div>

      {showLegend && (
        <div className="amc-legend">
          {snapshot.hasPromo && (
            <span className="amc-legend-item"><span className="amc-pill amc-pill-open amc-pill-promo amc-swatch" />{AVAILABILITY_IMAGE_COMPACT_LEGEND.promo}</span>
          )}
          {showCourtKey && (
            <span className="amc-legend-courts">
              {AVAILABILITY_IMAGE_COMPACT_LEGEND.courts}:
              {firstDayCourts.map(column => (
                <span key={column.number} className="amc-legend-court"><b>{column.court}</b> {column.label}</span>
              ))}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default MultiDayCompactTable;
